/**
 * One turn of the simulator thread, editable in place. The latest AI reply is styled apart,
 * and another surface can present a reply in its place (a ReplyView).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import { Pencil, Trash2, Check, X, Bot, ShieldAlert, ArrowLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { LucideIcon } from 'lucide-react';
import type { Message } from '@/modules/chatbot/types';
import { lastRunDetailsAtom } from '@/stores/websocket.store';
import { TurnBubble, TurnLabel, TurnRow } from '../ui/turn';
import { ROLE_META, otherRole } from './roles';

/** Another surface presenting this turn in place: the author line, the body, and whether the turn's edit controls still apply. */
export interface ReplyView {
  icon: LucideIcon;
  label: ReactNode;
  body: ReactNode;
  /** False while the bubble is showing something other than this turn's own text. */
  editable: boolean;
}

interface MessageRowProps {
  message: Message;
  view?: ReplyView;
  showActions: boolean;
  isLast: boolean;
  onUpdate?: (id: string, text: string) => void;
  onDelete?: (id: string) => void;
  /** Moves this turn to the other side: a mis-authored turn is re-cast, not retyped. */
  onSwapRole?: (id: string) => void;
}

const formatTime = (timestamp: Date): string =>
  timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const MessageRow = ({ message, view, showActions, isLast, onUpdate, onDelete, onSwapRole }: MessageRowProps) => {
  const meta = ROLE_META[message.role];
  const swapTo = ROLE_META[otherRole(message.role)];
  const runDetails = useAtomValue(lastRunDetailsAtom);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const draftRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && draftRef.current) {
      draftRef.current.focus();
      const len = draftRef.current.value.length;
      draftRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  // An AI reply is styled apart only while it is the latest turn; older ones read as
  // plain Agent turns, like replies already sent.
  const isAiReply = message.role === 'assistant' && !message.isError && Boolean(message.requestId);
  const showAiStyle = isAiReply && isLast;
  // The latest run's reply scored below the surfacing threshold: shown here, but it
  // would have been held back from the agent in production.
  const belowThreshold = showAiStyle && Boolean(runDetails?.belowConfidenceThreshold);
  const isError = Boolean(message.isError);
  const isRight = meta.align === 'right';

  const handleSave = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== message.content && onUpdate) {
      onUpdate(message.id, trimmed);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(message.content);
    setEditing(false);
  };

  return (
    <TurnRow side={isRight ? 'right' : 'left'} className='group animate-in fade-in-0 slide-in-from-bottom-1 duration-300'>
        <TurnLabel
          icon={view?.icon ?? (showAiStyle ? Bot : meta.icon)}
          iconClassName={view || showAiStyle ? 'text-indigo-500' : undefined}
        >
          {view ? (
            view.label
          ) : (
            <>
              <span className={cn('font-medium', showAiStyle && 'text-indigo-600')}>
                {showAiStyle ? 'AI · Agent' : meta.label}
              </span>
              <span>·</span>
              <span>{formatTime(message.timestamp)}</span>
            </>
          )}
          {belowThreshold && (
            <span className='inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700'>
              <ShieldAlert className='h-2.5 w-2.5' />
              Low confidence · held in production
            </span>
          )}
        </TurnLabel>
        <div className={cn('flex items-start gap-1.5', isRight && 'flex-row-reverse')}>
          <TurnBubble
            tone={isError ? 'error' : showAiStyle ? 'ai' : isRight ? 'agent' : 'customer'}
            className='whitespace-pre-wrap wrap-break-word'
          >
            {editing ? (
              <Textarea
                ref={draftRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSave();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    handleCancel();
                  }
                }}
                className='min-h-[60px] min-w-[260px] bg-transparent border-0 focus-visible:ring-0 p-0 text-sm resize-y shadow-none'
              />
            ) : (
              view?.body ?? message.content
            )}
          </TurnBubble>
          {showActions && (view?.editable ?? true) && (
            <div
              className={cn(
                'flex flex-col gap-0.5 transition-opacity',
                editing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              )}
            >
              {editing ? (
                <>
                  <Button size='icon' variant='ghost' className='h-6 w-6 text-emerald-600 hover:bg-emerald-50' onClick={handleSave} title='Save (⌘+Enter)'>
                    <Check className='h-3 w-3' />
                  </Button>
                  <Button size='icon' variant='ghost' className='h-6 w-6 text-muted-foreground hover:bg-gray-50' onClick={handleCancel} title='Cancel (Esc)'>
                    <X className='h-3 w-3' />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size='icon'
                    variant='ghost'
                    className='h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-gray-50'
                    onClick={() => {
                      setDraft(message.content);
                      setEditing(true);
                    }}
                    title='Edit'
                  >
                    <Pencil className='h-3 w-3' />
                  </Button>
                  <Button
                    size='icon'
                    variant='ghost'
                    className='h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-gray-50'
                    onClick={() => onSwapRole?.(message.id)}
                    title={`Switch to ${swapTo.label}`}
                    aria-label={`Switch to ${swapTo.label}`}
                  >
                    <ArrowLeftRight className='h-3 w-3' />
                  </Button>
                  <Button
                    size='icon'
                    variant='ghost'
                    className='h-6 w-6 text-muted-foreground hover:text-red-600 hover:bg-red-50'
                    onClick={() => onDelete?.(message.id)}
                    title='Delete'
                  >
                    <Trash2 className='h-3 w-3' />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
    </TurnRow>
  );
};
